import { supabase } from "@/integrations/supabase/client";
import type { PDFDocumentProxy } from "pdfjs-dist";

const THUMBNAIL_SCALE = 0.8; // Higher scale for crisp thumbnails
const THUMBNAIL_QUALITY = 0.92; // JPEG quality (0-1)

export function usePdfThumbnailUpload() {
  async function generateAndUploadThumbnail(
    pdf: PDFDocumentProxy,
    contentId: string
  ): Promise<string | null> {
    try {
      // Get page 1
      const page = await pdf.getPage(1);
      
      // Use device pixel ratio for retina displays
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const viewport = page.getViewport({ scale: THUMBNAIL_SCALE * dpr });

      // Create canvas and render at higher resolution
      const canvas = document.createElement("canvas");
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      const context = canvas.getContext("2d");

      if (!context) {
        console.error("[usePdfThumbnailUpload] Failed to get canvas context");
        return null;
      }

      await page.render({ canvasContext: context, viewport }).promise;

      // Convert to JPEG for smaller file size with good quality
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, "image/jpeg", THUMBNAIL_QUALITY)
      );

      if (!blob) {
        console.error("[usePdfThumbnailUpload] Failed to create blob");
        return null;
      }

      // Get current user
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        console.error("[usePdfThumbnailUpload] User not authenticated");
        return null;
      }

      // Upload to storage (use .jpg extension for JPEG)
      const fileName = `${user.id}/${contentId}.jpg`;

      const { error: uploadError } = await supabase.storage
        .from("content-thumbnails")
        .upload(fileName, blob, {
          contentType: "image/jpeg",
          upsert: true,
        });

      if (uploadError) {
        console.error("[usePdfThumbnailUpload] Upload error:", uploadError);
        return null;
      }

      // Get public URL
      const {
        data: { publicUrl },
      } = supabase.storage.from("content-thumbnails").getPublicUrl(fileName);

      // Update content record with thumbnail URL
      const { error: updateError } = await supabase
        .from("content")
        .update({ thumbnail_url: publicUrl })
        .eq("id", contentId);

      if (updateError) {
        console.error("[usePdfThumbnailUpload] Update error:", updateError);
        return null;
      }

      console.log("[usePdfThumbnailUpload] Thumbnail generated successfully");
      return publicUrl;
    } catch (error) {
      console.error("[usePdfThumbnailUpload] Error:", error);
      return null;
    }
  }

  return { generateAndUploadThumbnail };
}
